import { isNotNullish } from '@adornis/assert/isNullish.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { validate } from '@adornis/validation/decorators.js';
import type { InsertOneResult, UpdateResult } from 'mongodb';
import { clearPublishFilterCache } from '../../server/martinusPublishFilter.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { Validator } from '@adornis/validation/validator.js';
import { calculateErfahrungsstufe } from '../api/calculateErfahrungsstufe.js';
import { calculateWorkingHoursForEingruppierung } from '../api/calculateWorkingHours.js';
import { isPercentSumValid, kostenstellenSumErrorMessage } from '../api/kostenstellenPercent.js';
import { compareSupplements, ContractSupplement } from './ContractSupplement.js';
import { AGInfos } from './vertrag-sektionen/AGinfos.js';
import { AGSozialVersicherung } from './vertrag-sektionen/AGSozialversicherung.js';
import { AGEingruppierung1, AGEingruppierung2 } from './vertrag-sektionen/vertrag-eingruppierung/AGEingruppierung.js';

@Entity()
export class Contract extends MongoEntity {
  public static override _class = 'Contract';
  public static override _collectionName = 'contracts';

  @validate(nonOptional())
  @Field(() => ID)
  userID!: string;

  @Field(() => Date, { default: v => v ?? new Date() })
  createdAt!: Date;

  @Field(() => AGInfos, { default: v => v ?? new AGInfos({}) })
  agInfos: Maybe<AGInfos>;

  @Field(() => AGSozialVersicherung, { default: v => v ?? new AGSozialVersicherung({}) })
  agSozialVersicherung: Maybe<AGSozialVersicherung>;

  @Field(() => AGEingruppierung1, { default: v => v ?? new AGEingruppierung1({}) })
  agEingruppierung1: Maybe<AGEingruppierung1>;

  @Field(() => AGEingruppierung2)
  agEingruppierung2: Maybe<AGEingruppierung2>;

  /**
   * The person which creates the contract should tell here why it was created.
   */
  @Field(() => String)
  justification: Maybe<string>;

  @Field(() => [ContractSupplement], {
    resolve: function (this: Contract) {
      return async () => {
        const supplements = await loadMongoData(ContractSupplement, { vertragID: this._id });

        return supplements.sort(compareSupplements).map(c => constructValue(c) as ContractSupplement);
      };
    },
  })
  supplements: Maybe<ContractSupplement[]>;

  @Dependencies({
    classes: () => [],
    ids: () => [],
  })
  @Field(() => Int, {
    resolve: function (this: Contract) {
      return () => calculateErfahrungsstufe(this, '1')?.stufe;
    },
  })
  erfahrungsstufeAg1: Maybe<Int>;

  @Dependencies({
    classes: () => [],
    ids: () => [],
  })
  @Field(() => Int, {
    resolve: function (this: Contract) {
      return () => calculateErfahrungsstufe(this, '2')?.stufe;
    },
  })
  erfahrungsstufeAg2: Maybe<Int>;

  @Dependencies({
    classes: () => [],
    ids: () => [],
  })
  @Field(() => String, {
    resolve: function (this: Contract) {
      return () => {
        const allSchools = [
          ...(this.agEingruppierung1?.agVertragsInfos?.kostenstelleProzentsaetze?.map(k => ({
            school: k.kostenstelle?.school?.name ?? null,
            prozent: k.prozentsatz,
          })) ?? []),
          ...(this.agEingruppierung2?.agVertragsInfos?.kostenstelleProzentsaetze?.map(k => ({
            school: k.kostenstelle?.school?.name ?? null,
            prozent: k.prozentsatz,
          })) ?? []),
        ];

        const allSchoolsFiltered = allSchools.filter(isNotNullish);
        if (allSchoolsFiltered.length === 0) return null;
        const schoolWithMostPercent = allSchoolsFiltered.reduce((a, b) =>
          a.prozent && b.prozent ? (a.prozent > b.prozent ? a : b) : a,
        );
        return schoolWithMostPercent.school;
      };
    },
  })
  hauptarbeitgeber: Maybe<string>;

  static override async afterCreate(entity: Contract, result: InsertOneResult): Promise<void> {
    // the publish filter caches the readable contract ids per session, so without this reset the creator
    // cannot read back the contract they just created (the Schulleitung dies at notifyMAV right after)
    clearPublishFilterCache();
    await super.afterCreate(entity, result);
    await contractChanged(entity.userID);
  }

  static override async afterUpdate(
    oldEntity: PartialEntityData<Contract>,
    newEntity: Contract,
    result: UpdateResult,
  ): Promise<void> {
    await super.afterUpdate(oldEntity, newEntity, result);
    await contractChanged(newEntity.userID ?? oldEntity.userID);
  }

  static override async afterRemove(id: string, entityData: PartialEntityData<Contract>): Promise<void> {
    await super.afterRemove(id, entityData);
    await contractChanged(entityData?.userID);
  }
}

/**
 * The person's employee bookings and roles follow every change to a contract. Imported lazily: the reactions reach
 * back to this entity.
 *
 * A failure is logged, not thrown: the contract is saved by then, and failing the request would only make the user
 * save again what is already there. The nightly permission sync picks the person up again.
 */
export async function contractChanged(personID: Maybe<string>): Promise<void> {
  if (!personID) return;
  try {
    const { onContractChanged } = await import('../../server/reactions.js');
    await onContractChanged(personID);
  } catch (err) {
    logger.error({ err, personID }, 'Employee bookings and roles could not follow a contract change');
  }
}

if (A.isClient) {
  Contract.prototype.validator = new Validator({
    createdAt: options => {
      const ag1Kostenstellen = (
        (options.source as Readonly<Contract>).agEingruppierung1?.agVertragsInfos?.kostenstelleProzentsaetze?.map(
          k => k.prozentsatz,
        ) ?? []
      ).filter(isNotNullish);
      const ag2Kostenstellen = (
        (options.source as Readonly<Contract>).agEingruppierung2?.agVertragsInfos?.kostenstelleProzentsaetze?.map(
          k => k.prozentsatz,
        ) ?? []
      ).filter(isNotNullish);

      // validate only ag1
      const agEingruppierung1 = (options.source as Contract).agEingruppierung1;
      if (agEingruppierung1) {
        const sumPercentAg1 = ag1Kostenstellen.reduce((a, b) => a + b, 0);
        if (!isPercentSumValid(sumPercentAg1))
          throw new ValidationError(
            kostenstellenSumErrorMessage(
              'AG1',
              sumPercentAg1,
              calculateWorkingHoursForEingruppierung(agEingruppierung1),
            ),
          );
      }

      // validate only ag2
      const agEingruppierung2 = (options.source as Contract).agEingruppierung2;
      if (agEingruppierung2) {
        const sumPercentAg2 = ag2Kostenstellen.reduce((a, b) => a + b, 0);
        if (!isPercentSumValid(sumPercentAg2))
          throw new ValidationError(
            kostenstellenSumErrorMessage(
              'AG2',
              sumPercentAg2,
              calculateWorkingHoursForEingruppierung(agEingruppierung2),
            ),
          );
      }
    },
  });
}
